import React, { useCallback, useState } from 'react';
import { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DashboardView from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardView';
import DashboardHeader from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardHeader';
import useTypeEntity from '../useTypeEntity';
import { useUserSettingState } from '../../../../../../redux/user/UserSettingsAPI/hook/useUserSettingState';
import WorkspaceAPI from '../../../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import useListPanels from './useListPanels';
import Button from '../../../../../../theme/ui/Atoms/Button';
import DashboardAddPanelModal from '../../../../../../theme/ui/Dashboard/DashboardView/DashboardAddPanelModal';
import useTypeRequest from './useTypeRequest';

interface EntityDashboardProps {
  workspaceId?: string;
  groupId?: string;
}

function EntityDashboard({ workspaceId, groupId }: EntityDashboardProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [rowHeight] = useState(90);
  const [xsColumns] = useState(6);
  const { type } = useTypeEntity();
  WorkspaceAPI.useGetWorkspaceByIdQuery(workspaceId ?? skipToken);
  const typeRequest = useTypeRequest(workspaceId, groupId);

  const panels = useListPanels(typeRequest, workspaceId, groupId);
  const defaultLayout: Layout = [
    {
      i: 'stores',
      x: 0,
      y: 0,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
    {
      i: 'commander-offline',
      x: 3,
      y: 0,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
    {
      i: 'gateway-error',
      x: 0,
      y: 2,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
    {
      i: 'smart-switch-device-error',
      x: 3,
      y: 2,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
    {
      i: 'flexeserve-device-connection-error',
      x: 0,
      y: 4,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
    {
      i: 'flexeserve-device-error',
      x: 3,
      y: 4,
      w: 3,
      h: 2,
      moved: false,
      static: true,
    },
  ];
  const scope = `${type}:${groupId ?? workspaceId ?? '*'}`;
  const {
    data: layout,
    setData: setLayout,
    isLoading: isLoadingLayout,
  } = useUserSettingState(`workspaceDashboardLayout:${scope}`, defaultLayout);
  // availablePanels must be defined after both panels and layout
  const availableToAddPanels = React.useMemo(
    () => panels.filter((p) => !layout?.some((l) => l.i === p.id)),
    [layout, panels]
  );

  const visiblePanels = React.useMemo(
    () => panels.filter((p) => layout?.some((l) => l.i === p.id)),
    [layout, panels]
  );

  const handleLayoutChange = (newLayout: Layout) => {
    if (isEditMode) {
      setLayout(newLayout.map((item) => ({ ...item, static: !isEditMode })));
    }
  };

  const onToggleEditMode = () => {
    if (layout)
      setLayout(
        layout.map((item) => ({ ...item, static: isEditMode })),
        isEditMode
      );
    setIsEditMode(!isEditMode);
  };

  // reset edit mode
  React.useEffect(() => {
    setIsEditMode(false);
  }, [groupId, workspaceId]);

  const handleRemovePanel = (id: string) => {
    layout && setLayout(layout.filter((l) => l.i !== id));
  };

  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const handleAddPanelClick = () => {
    setIsAddPanelOpen(true);
  };
  const handleCloseAddPanel = () => {
    setIsAddPanelOpen(false);
  };
  const handleSavePanel = (panelId: string) => {
    const y = layout ? Math.max(...layout.map((l) => l.y + l.h)) : 0;
    setLayout([
      ...(layout || []),
      { i: panelId, x: 0, y, w: 3, h: 2, static: !isEditMode },
    ]);
    setIsAddPanelOpen(false);
  };
  const parseRatio = (ratio?: string | number) => {
    if (typeof ratio === 'number') return ratio;
    if (!ratio) return 1;

    const [w, h] = ratio.split('/').map(Number);
    return w / h;
  };
  const createResizeHandler = () => {
    return (
      l: LayoutItem,
      oldLayoutItem: LayoutItem,
      layoutItem: LayoutItem,
      placeholder: LayoutItem
    ) => {
      const panel = panels.find((p) => p.id === layoutItem.i);

      if (panel && !panel.aspectRatio) return; // only apply auto-resize for panels with aspect ratio defined
      const ratio = parseRatio(panel?.aspectRatio);

      const heightDiff = layoutItem.h - oldLayoutItem.h;
      const widthDiff = layoutItem.w - oldLayoutItem.w;

      if (Math.abs(heightDiff) < Math.abs(widthDiff)) {
        const newH = Math.round(layoutItem.w / ratio);

        layoutItem.h = newH;
        placeholder.h = newH;
      } else {
        const newW = Math.round(layoutItem.h * ratio);

        layoutItem.w = newW;
        placeholder.w = newW;
      }
    };
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(createResizeHandler(), []);
  // const title =
  //   entity?.name || workspace?.name
  //     ? `${entity?.name || workspace?.name} ${t('dashboard.title')}`
  //     : t('dashboard.title');
  return (
    <DashboardView
      panels={visiblePanels}
      layout={layout}
      isEditMode={isEditMode}
      rowHeight={rowHeight}
      xsColumns={xsColumns}
      theme={theme}
      isLoadingLayout={isLoadingLayout}
      onRemovePanel={handleRemovePanel}
      onLayoutChange={handleLayoutChange}
      handleResize={handleResize}
      header={
        <>
          {/* <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h4">{title}</Typography>
          </Box> */}
          <DashboardHeader
            isEditMode={isEditMode}
            onToggleEditMode={onToggleEditMode}
            loading={isLoadingLayout}
          />

          {isEditMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddPanelClick}
              disabled={availableToAddPanels.length === 0}
            >
              {t('dashboard.add_panel', 'Add Panel')}
            </Button>
          )}
          <DashboardAddPanelModal
            open={isAddPanelOpen}
            onClose={handleCloseAddPanel}
            onSavePanel={handleSavePanel}
            availablePanels={availableToAddPanels}
          />
        </>
      }
    />
  );
}

export default EntityDashboard;
